/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View } from '@components/atoms'
import { useLocation, useNavigate } from 'react-router-dom'
import { mono, monoFont } from './monolith.tokens'

import config from '@configs'
import { HomeNavMenuType } from '@redux/types'
import { useAppSelector } from '@redux/index'

/**
 * 계정 영역 가로 네비 — Monolith(2안).
 *
 * DESIGN_SYSTEM §5 가 사이드 네비(HomeSideNav)를 금지해서, 계정 4페이지의 이동은
 * 헤어라인 한 줄 위의 가로 링크로 처리한다. 스타일은 §3 "네비 메뉴" 규격 그대로 —
 * 13px 500 faint, 활성 = ink + 1px 밑줄.
 *
 * 항목 SSOT 는 기존 config.customerMenu() (menu.config.ts) 를 그대로 쓴다.
 *
 * 2026-09-26: 계정 페이지의 제목·리드를 걷어내 이 네비가 페이지 맨 위에 선다. 역할 항목
 * (Wholesale Orders · Performance)은 `customer.roles` 가 있을 때만 든다 (PARTNER_PLAN §2).
 */
export const MonolithAccountNav = (): JSX.Element => {
	// hook
	const location = useLocation()
	const navigate = useNavigate()

	// redux state
	const customerRoles: string[] = useAppSelector(
		state => state.customerAuth.customer.roles,
	)

	const menuList: HomeNavMenuType[] = config.customerMenu(customerRoles)

	return (
		<View css={style} className="mnAccountNav">
			<nav className="mnAccountNavInner" aria-label="Account">
				{menuList.map(aMenu => {
					// 하위 주소(/customer/messages/:id)에서도 그 메뉴가 켜진다
					const isActive: boolean =
						location.pathname === aMenu.uri ||
						location.pathname.startsWith(`${aMenu.uri}/`)

					return (
						<button
							key={aMenu.id}
							type="button"
							className={`mnAccountNavItem ${isActive ? 'on' : ''}`}
							aria-current={isActive ? 'page' : undefined}
							onClick={() => navigate(aMenu.uri)}
						>
							{aMenu.title}
						</button>
					)
				})}
			</nav>
		</View>
	)
}

// 2026-10-06 위 헤어라인 삭제(사용자 지시) — 상단 바의 아래 선과 겹쳐 두 줄로 보였다
const style = css({
	width: '100%',

	'.mnAccountNavInner': {
		display: 'flex',
		flexWrap: 'wrap',
		gap: 36,
		padding: '22px 0',

		'.mnAccountNavItem': {
			padding: 0,
			border: 0,
			background: 'transparent',
			fontFamily: monoFont.sans,
			fontSize: 13,
			fontWeight: 500,
			color: mono.faint,
			cursor: 'pointer',
			borderBottom: '1px solid transparent',
			paddingBottom: 2,
			transition: 'color 200ms ease-out',

			'&:hover': {
				color: mono.ink,
			},
			'&.on': {
				color: mono.ink,
				borderBottomColor: mono.ink,
			},
		},

		'@media (max-width: 600px)': {
			gap: 22,
		},
	},

	'@media (prefers-reduced-motion: reduce)': {
		'.mnAccountNavInner .mnAccountNavItem': {
			transition: 'none',
		},
	},
})
